import {useMemo} from 'react'

import {BOARD_LANE_LABELS, BOARD_NEEDS_ACTION_VIEW_LABEL} from './board-lane-labels'
import type {BoardLane} from './board-lanes'
import {type RankedLaneSections, rankLaneSections} from './board-sections'
import {splitCleanUp} from './clean-up'
import {type BoardCardVariant, DispatchBoardCard} from './dispatch-board-card'
import {buildJobCardSummary, type JobCardHorizonContext} from './job-card-summary'
import type {TimelineJobRow} from './job-rows'
import {buildHorizonContext} from './operational-ranking'
import {laneStickyHeaderClass} from './swim-lane-sticky'
import {
    expandJobsAcrossDays,
    jobsVisibleInHorizon,
    type TerminalRowEntry,
    todayViewOffset,
} from './terminal-jobs'
import type {DispatchTimelineData, UnassignedJob, ViewRange} from './types'
import classes from './widescreen-board.module.css'

export type WidescreenBoardProps = {
    data: DispatchTimelineData
    viewRange: ViewRange
    onJobPress: (job: UnassignedJob) => void
    onCommittedPress?: (job: TimelineJobRow) => void
    highlightAssignmentId?: string | null
}

const LANE_ORDER: {lane: BoardLane; key: keyof RankedLaneSections}[] = [
    {lane: 'needs_action', key: 'needsAction'},
    {lane: 'notify_drivers', key: 'notifyDrivers'},
    {lane: 'dispatched', key: 'dispatched'},
]

/** Shared board data: ranked lane sections for a projected view. */
export function useWidescreenBoard(data: DispatchTimelineData, viewRange: ViewRange) {
    const visibleDays = data.visibleDays ?? [data.scheduleDate]
    const dayCount = visibleDays.length

    const {sections, cleanUp} = useMemo<{
        sections: RankedLaneSections
        cleanUp: TerminalRowEntry[]
    }>(() => {
        const jobs = jobsVisibleInHorizon(data)
        const viewDayOriginOffset = data.viewDayOriginOffset ?? 0
        const entries = expandJobsAcrossDays(
            jobs,
            dayCount,
            viewDayOriginOffset,
            todayViewOffset(visibleDays),
        )
        const split = splitCleanUp(entries)
        return {
            sections: rankLaneSections(split.current, buildHorizonContext(data, viewRange)),
            cleanUp: split.cleanUp,
        }
    }, [data, dayCount, viewRange, visibleDays])

    return {sections, cleanUp}
}

export function pressEntry(
    entry: TerminalRowEntry,
    onJobPress: (job: UnassignedJob) => void,
    onCommittedPress?: (job: TimelineJobRow) => void,
) {
    if (entry.job.unassignedJob) {
        onJobPress(entry.job.unassignedJob)
        return
    }
    onCommittedPress?.(entry.job)
}

export function BoardCardFromEntry({
    entry,
    variant,
    highlighted,
    onPress,
    horizon,
}: {
    entry: TerminalRowEntry
    variant: BoardCardVariant
    highlighted: boolean
    onPress: () => void
    horizon: JobCardHorizonContext
}) {
    const summary = buildJobCardSummary(entry, horizon)
    return (
        <DispatchBoardCard
            summary={summary}
            variant={variant}
            highlighted={highlighted}
            onPress={onPress}
        />
    )
}

export function LaneHeading({lane, count}: {lane: BoardLane; count: number}) {
    return (
        <div
            className={`${classes.laneHeading} ${classes[`laneHeading_${lane}`]} ${laneStickyHeaderClass(lane)}`}
        >
            <span className={classes.laneHeadingLabel}>
                {lane === 'needs_action' ? BOARD_NEEDS_ACTION_VIEW_LABEL : BOARD_LANE_LABELS[lane]}
            </span>
            <span className={classes.laneCount}>{count}</span>
        </div>
    )
}

export {LANE_ORDER}
